import { Account } from '../../types/interfaces/account.interfaces';
import {
  OnFarmAccountBilling,
  OnFarmAccountsBillingResponse,
  OnFarmBillingPreview,
} from '../../types/interfaces/onFarmBillingOverride.interfaces';
import { PagedResponse } from '../../types/interfaces/apiParams.interfaces';
import { centsToDollars } from '../../services/account.service';
import { ON_FARM_BILLING_CONSTANTS } from '../../constants/common';

export enum OnFarmBillingFilter {
  ALL = 'All',
  CUSTOM_QUOTE = 'Custom Quote',
  OUTSTANDING = 'Outstanding',
  LOCKED = 'Locked',
}

export type OnFarmBillingAccount = Account & {
  isCustomQuote?: boolean;
  monthlyFeeCents?: number | null;
  planMonthlyFeeCents?: number | null;
  maxTanks?: number | null;
  activeTankCount?: number | null;
  isUsingOnFarm?: boolean;
  status?: string;
};

export const toList = <T>(data: T[] | PagedResponse<T> | undefined): T[] => {
  if (Array.isArray(data)) return data;
  return data?.result ?? [];
};

export const getOnFarmAccountStatus = (isOnFarmBlocked?: boolean): string =>
  isOnFarmBlocked ? ON_FARM_BILLING_CONSTANTS.LOCKED : ON_FARM_BILLING_CONSTANTS.ACTIVE;

export const getOnFarmStatusClass = (status?: string): string => {
  if (status === ON_FARM_BILLING_CONSTANTS.LOCKED || status === ON_FARM_BILLING_CONSTANTS.PAST_DUE) {
    return 'on-farm-status on-farm-status--alert';
  }
  if (status === ON_FARM_BILLING_CONSTANTS.NOT_STARTED || status === ON_FARM_BILLING_CONSTANTS.NOT_USING) {
    return 'on-farm-status on-farm-status--pending';
  }
  return 'on-farm-status on-farm-status--success';
};

export const formatOnFarmCents = (cents?: number | null): string => {
  if (cents == null) return ON_FARM_BILLING_CONSTANTS.NOT_PROVIDED;
  return `$${centsToDollars(cents)}`;
};

export const formatOnFarmValue = (value?: string | number | null): string => {
  if (value == null || value === '') return ON_FARM_BILLING_CONSTANTS.NOT_PROVIDED;
  return String(value);
};

export const formatOnFarmPlan = (isCustomQuote?: boolean): string => {
  if (isCustomQuote === true) return ON_FARM_BILLING_CONSTANTS.CUSTOM_QUOTE;
  if (isCustomQuote === false) return ON_FARM_BILLING_CONSTANTS.STANDARD_PLAN;
  return ON_FARM_BILLING_CONSTANTS.NOT_PROVIDED;
};

export const buildOnFarmBilledAccountsQuery = (status: OnFarmBillingFilter): { filter?: string } => {
  if (status === OnFarmBillingFilter.OUTSTANDING) {
    return { filter: 'OnFarmOutstandingBalanceCents gt 0' };
  }
  if (status === OnFarmBillingFilter.LOCKED) {
    return { filter: 'IsOnFarmBlocked eq true' };
  }
  if (status === OnFarmBillingFilter.CUSTOM_QUOTE) {
    return { filter: 'IsCustomQuote eq true' };
  }
  return {};
};

export const toAccountFromOnFarmBilling = (item: OnFarmAccountBilling): OnFarmBillingAccount => ({
  accountId: item.accountId,
  name: item.accountName,
  contactFirstName: item.contactFirstName ?? '',
  contactLastName: item.contactLastName ?? '',
  contactEmail: item.contactEmail ?? '',
  contactPhone: item.contactPhone ?? '',
  owner: { userId: item.users?.[0]?.userId ?? 0 },
  onFarmBillingStartDate: item.onFarmBillingStartDate,
  isOnFarmBlocked: item.isOnFarmBlocked,
  onFarmOutstandingBalanceCents: item.onFarmOutstandingBalanceCents,
  isCustomQuote: item.isCustomQuote,
  monthlyFeeCents: item.planMonthlyFeeCents ?? item.monthlyFeeCents,
  planMonthlyFeeCents: item.planMonthlyFeeCents,
  maxTanks: item.maxTanks,
  activeTankCount: item.activeTankCount,
  isUsingOnFarm: item.isUsingOnFarm,
  status: getOnFarmAccountStatus(item.isOnFarmBlocked),
});

export const toPagedAccountsFromOnFarmBilling = (response?: OnFarmAccountsBillingResponse) => ({
  result: (response?.accounts ?? []).map(toAccountFromOnFarmBilling),
  total: response?.totalAccountsCount ?? 0,
});

export const toAccountFromPreview = (account: Account, preview?: OnFarmBillingPreview): OnFarmBillingAccount => ({
  ...account,
  onFarmOutstandingBalanceCents: preview?.onFarmOutstandingBalanceCents ?? account.onFarmOutstandingBalanceCents,
  isOnFarmBlocked: preview?.isOnFarmBlocked ?? account.isOnFarmBlocked,
  onFarmBillingStartDate: preview?.onFarmBillingStartDate ?? account.onFarmBillingStartDate,
  isCustomQuote: preview?.isCustomQuote,
  monthlyFeeCents: preview?.planMonthlyFeeCents ?? preview?.monthlyFeeCents,
  planMonthlyFeeCents: preview?.planMonthlyFeeCents,
  maxTanks: preview?.maxTanks,
  activeTankCount: preview?.activeTankCount,
  isUsingOnFarm: preview?.isUsingOnFarm,
  status: getOnFarmAccountStatus(preview?.isOnFarmBlocked ?? account.isOnFarmBlocked),
});
